import { APP_INITIALIZER, ErrorHandler, isDevMode, NgModule } from '@angular/core';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { BrowserModule } from '@angular/platform-browser';
import { TitleStrategy } from '@angular/router';
import { OAuthModule, OAuthService } from 'angular-oauth2-oidc';
import { BrainTitleStrategy } from 'shared/services/title/brain-title-strategy.service';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { StoreModule } from '@ngrx/store';
import { EffectsModule } from '@ngrx/effects';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { metaReducers, reducers } from './reducers';
import { FacilityEffects } from './brain-data/store/effects/facility.effects';
import { HTTP_INTERCEPTORS, HttpClientJsonpModule, HttpClientModule } from '@angular/common/http';
import { UtilityEffects } from 'brain-data/store/effects/utility.effects';
import { UtilityReportEffects } from './brain-data/store/effects/utility-report.effects';
import { UtilityContractsEffects } from 'brain-data/store/effects/utility-contract.effects';
import { AuthenticationEffects } from 'brain-data/store/effects/authentication.effects';
import { BearerInterceptor } from 'interceptors/bearer.interceptor';
import { CorrelationIdInterceptor } from 'interceptors/correlation-id.interceptor';
import { HttpErrorInterceptor } from 'interceptors/http-error.interceptor';
import { UtilityBillsEffects } from 'brain-data/store/effects/utility-bills.effects';
import { ExternalProviderEffects } from 'brain-data/store/effects/external-provider.effects';
import * as Sentry from '@sentry/angular-ivy';
import { ErrorNotifitcationEffects } from 'brain-data/store/effects/error-notification.effects';
import { SubSystemEffects } from './brain-data/store/effects/sub-system.effects';
import { EmissionsEffects } from 'brain-data/store/effects/emissions.effects';
import { ReportEffects } from 'brain-data/store/effects/report.effects';
import { UIEffects } from '@brain-portal/store/ui.effects';
import { AlertEffects } from 'brain-data/store/effects/alert.effects';
import { UserEffects } from 'brain-data/store/effects/user.effects';
import { RoleEffects } from 'brain-data/store/effects/role.effects';
import { StartupService } from 'shared/services/utils/startup/startup.service';
import { prefersReducedMotion } from 'shared/services/utils/motion/motion.service';
import { RuntimeConfigService } from 'shared/services/runtime-config/runtime-config.service';
import { ConfigEffects } from 'brain-data/store/effects/config.effects';
import { ChargepointEffects } from 'brain-data/store/effects/chargepoint.effects';
import { EnergyStarItemEffects } from 'brain-data/store/effects/energy-star-item.effects';
import { EnergyStarAccountEffects } from 'brain-data/store/effects/energy-star-account.effects';
import { ProviderEffects } from '../../brain-data/store/effects/provider.effect';
import { LeaseEffects } from '../../brain-data/store/effects/lease.effects';
import { SystemIntegrationEffects } from 'brain-data/store/effects/system-integration.effects';
import { AccessLogEffects } from 'brain-data/store/effects/access-log.effects';
import { ResponsibleOfficialEffects } from 'brain-data/store/effects/responsible-official.effects';
import { UtilityRateEffects } from 'brain-data/store/effects/utility-rate.effects';
import { CustomFieldDefinitionEffects } from 'brain-data/store/effects/custom-field-definition.effects';
import { TutorialVideoEffects } from 'brain-data/store/effects/tutorial-video.effects';
import { TariffEffects } from 'brain-data/store/effects/tariff.effects';
import { ContractRawValuesEffects } from 'brain-data/store/effects/contract-raw-values.effects';
import { EstreamPanelEffects } from 'brain-data/store/effects/estream-panel.effects';
import { EstreamComputeEffects } from 'brain-data/store/effects/estream-compute.effects';
import { EstreamComputeRangeEffects } from 'brain-data/store/effects/estream-compute-range.effects';
import { EstreamContractStatusEffects } from 'brain-data/store/effects/estream-contract-status.effects';
import { AgentChatLauncherComponent } from 'shared/components/agent-chat-launcher/agent-chat-launcher.component';
import { AgentChatPanelComponent } from 'shared/components/agent-chat-panel/agent-chat-panel.component';

@NgModule({
  declarations: [AppComponent, AgentChatLauncherComponent, AgentChatPanelComponent],
  imports: [
    // Complements the prefers-reduced-motion block in styles.scss: Angular drives Material/CDK
    // animations through its own engine (partly the Web Animations API), outside the CSS cascade,
    // so this swaps in NoopAnimationDriver directly (evaluated once at bootstrap, not live).
    BrowserAnimationsModule.withConfig({ disableAnimations: prefersReducedMotion() }),
    BrowserModule,
    AppRoutingModule,
    OAuthModule.forRoot(),
    StoreModule.forRoot(reducers, {
      metaReducers,
      runtimeChecks: {
        strictStateImmutability: true,
        strictActionImmutability: true,
      },
    }),
    EffectsModule.forRoot([
      AlertEffects,
      ConfigEffects,
      EmissionsEffects,
      FacilityEffects,
      UtilityEffects,
      UtilityReportEffects,
      UtilityBillsEffects,
      UtilityContractsEffects,
      AuthenticationEffects,
      ExternalProviderEffects,
      SubSystemEffects,
      ErrorNotifitcationEffects,
      ReportEffects,
      UserEffects,
      RoleEffects,
      UIEffects,
      ChargepointEffects,
      EnergyStarItemEffects,
      EnergyStarAccountEffects,
      ProviderEffects,
      LeaseEffects,
      SystemIntegrationEffects,
      AccessLogEffects,
      ResponsibleOfficialEffects,
      TariffEffects,
      ContractRawValuesEffects,
      EstreamPanelEffects,
      EstreamComputeEffects,
      EstreamComputeRangeEffects,
      EstreamContractStatusEffects,
      UtilityRateEffects,
      CustomFieldDefinitionEffects,
      TutorialVideoEffects,
    ]),
    StoreDevtoolsModule.instrument({ maxAge: 25, logOnly: !isDevMode(), connectInZone: true }),
    HttpClientModule,
    HttpClientJsonpModule,
  ],
  providers: [
    { provide: HTTP_INTERCEPTORS, useClass: CorrelationIdInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: BearerInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: HttpErrorInterceptor, multi: true },
    { provide: TitleStrategy, useClass: BrainTitleStrategy },
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({
        showDialog: false,
      }),
    },
    {
      provide: APP_INITIALIZER,
      useFactory: (myStartupService: StartupService) => () => myStartupService.initializeApp(),
      deps: [StartupService],
      multi: true,
    },
    // Separate from StartupService's initializer above (which is fire-and-forget/void, no ordering
    // guarantee) -- this one's factory returns RuntimeConfigService.load()'s Promise, which
    // ApplicationInitStatus awaits before AppComponent (or any other component) is created, so every
    // Google Maps consumer sees an already-resolved key. load() always resolves (bounded timeout +
    // catchError internally) so this can never block app boot indefinitely.
    {
      provide: APP_INITIALIZER,
      useFactory: (runtimeConfigService: RuntimeConfigService) => () => runtimeConfigService.load(),
      deps: [RuntimeConfigService],
      multi: true,
    },
  ],

  bootstrap: [AppComponent],
})
export class AppModule {
  constructor(private oauthService: OAuthService) {
    this.oauthService.setStorage(localStorage);
    void this.oauthService.tryLogin();
  }
}
